import React from 'react';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import RouteHeader from './RouteHeader';

/**
 * PageHeader — envelope canônico do header via setPageHeader do MainPage.
 * Compõe (back chevron opcional) + <RouteHeader> + (actions opcional) —
 * antes cada caller repetia esse Fragment à mão. Consumidores em `common/`
 * evita chevron/kebab desalinhados entre telas.
 *
 * <p>Uso:
 * ```jsx
 * setPageHeader(
 *   <PageHeader
 *     backTo="/console/schools"
 *     backLabel={t('schools.back')}
 *     crumbs={[...]}
 *     title="Nome"
 *     actions={<Dropdown>...</Dropdown>}
 *   />
 * );
 * ```
 *
 * <p><b>Nota — PanelStudent NÃO usa</b> porque tem wrapper próprio
 * (`.pnl-header pnls-header`) que dá gap 12px e min-width. Uso opt-in;
 * quando fizer sentido migrar o Painel, adicionar prop `className` aqui.
 *
 * @param {string}   [backTo]     rota do chevron. Omitir = sem chevron.
 * @param {object}   [backState] estado levado no chevron — o recorte da
 *                                listagem, no fluxo lista → entidade.
 * @param {function} [onBack]     voltar que NÃO é rota (drill-down por estado).
 *                                Ignorado quando `backTo` vier junto.
 * @param {string}   [backLabel]  aria-label + title do chevron.
 * @param {array}    crumbs       array de {label, to?} pro <RouteHeader>.
 * @param {node}     title        título da rota (string ou JSX).
 * @param {node}     [actions]    kebab, botão de ação, etc. Renderizado
 *                                depois do RouteHeader. Omitido enquanto
 *                                `loading`.
 * @param {boolean}  [loading]    shimmera o TÍTULO (crumbs e seta ficam
 *                                reais). Ver o comentário no corpo.
 */
export default function PageHeader({ backTo, backState, backLabel, onBack, crumbs, title, actions, loading = false }) {
	/**
	 * 🔴 O CRUMB QUE LEVA AO MESMO LUGAR DO CHEVRON LEVA O MESMO CONTEXTO
	 * `[PO DECIDIU 23/10/2026]`.
	 *
	 * <p>O PO mediu em tela: vindo do "Em uso" de uma questão para a lista, a
	 * SETA devolvia a questão na aba Classificação e o CRUMB ao lado devolvia a
	 * mesma questão na aba padrão. Dois gatilhos, um destino, duas respostas.
	 *
	 * <p>⚠️ A injeção é por IGUALDADE de destino (`to === backTo`), não por
	 * posição: crumb de nível acima ("Studio") não tem nada a ver com o recorte
	 * de quem chamou, e levar o `state` para ele plantaria contexto em tela que
	 * não o pediu.
	 * <p>⚠️ Mora AQUI e não em cada tela porque são cinco usando `backState`
	 * hoje (`QuizDetail`, `ExamDetail`, `QuestionDetail`, `SchoolClassDetail`,
	 * `SaveUser`) e o esquecimento seria exatamente o defeito acima, uma tela de
	 * cada vez. Quem não passa `backState` não muda de comportamento.
	 */
	const crumbsWithState = (crumbs || []).map(c => (
		backState && c && c.to && c.to === backTo ? { ...c, state: backState } : c
	));

	/**
	 * Shim do título (L2) enquanto o objeto carrega.
	 *
	 * <p>⚠️ Só o TÍTULO shimmera. Seta de voltar e crumbs continuam REAIS: são
	 * estáticos, já se sabem antes do fetch, e é o crumb que confirma "cliquei
	 * certo". Régua herdada do `PanelStudentHeader` (loading system, fatia C):
	 * <b>mostrar apagado o que você sabe é pior que shimmerar o que falta</b>.
	 *
	 * <p>⚠️ `actions` não renderiza durante o load — kebab sobre um objeto que
	 * ainda não existe oferece ação que não tem em quê agir.
	 *
	 * <p>Altura 20px casa o `--shell-h1-size` (19px Manrope 800) — shim mais
	 * baixo que o texto faz o header "pular" quando o nome chega.
	 */
	const titleNode = loading
		? <span className="sk" style={{ width: 220, height: 20, borderRadius: 5 }} aria-hidden="true" />
		: title;
	return (
		<>
			{backTo && (
				<Link
					to={backTo}
					/* ⚠️ `state` é prop do `Link`, não parte do `to`: o `to` só carrega
					   caminho. Quem usa isto é o fluxo listagem → entidade, que devolve
					   o recorte da lista pelo `state` em vez da query. */
					state={backState}
					className="back-btn"
					aria-label={backLabel}
					title={backLabel}
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</Link>
			)}
			{/* ⚠️ `onBack` para o voltar que NÃO é rota (2026-09-11). O Log de
			    Mensagens abre a timeline por estado, não por URL: sem isto, a única
			    saída era um "← Voltar" solto no corpo, e voltar é ação de CABEÇALHO —
			    o corpo é onde se lê, não onde se navega.
			    ⚠️ Mesma classe `.back-btn` do `<Link>`: dois jeitos de voltar com
			    aparências diferentes é o que faz a pessoa procurar a seta no lugar
			    errado na tela seguinte.
			    ⚠️ `backTo` GANHA quando os dois vierem — rota é mais forte que
			    estado, e um cabeçalho com duas setas não tem leitura. */}
			{!backTo && onBack && (
				<button
					type="button"
					onClick={onBack}
					className="back-btn"
					aria-label={backLabel}
					title={backLabel}
				>
					<FontAwesomeIcon icon={faChevronLeft} />
				</button>
			)}
			<RouteHeader crumbs={crumbsWithState} title={titleNode} />
			{!loading && actions}
		</>
	);
}
